// app/components/global-product-modal.tsx
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '~/context/modal-context';
import ModalProductCard from '~/components/product-modal/modal-product-card';
import ShopNearbyDispensaries from '~/components/shop-nearby-dispensaries';
import { useStore } from '~/context/store-context';
import { Link, useLocation } from '@remix-run/react';
import { analytics } from '~/utils/analytics';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { createCache, createCacheKey, CACHE_PRESETS } from '~/utils/cache-manager';

interface Product {
  id: string;
  name: string;
  strain?: string;
  image: string;
  brand: string;
  category: string;
  price: number;
  salePrice?: number;
  loyaltyTierLabel?: string;
  discount?: string;
  product_description: string;
  thc: string;
  size: string;
  subtype: string;
  classification: string;
  lab_results: string;
  slug: string;
  stock?: number;
  sellable_quantity?: number;
  budflow_mup?: {
    active: boolean;
    products: string[];
    rule_config: {
      quantity?: number;
      target_price?: number;
      discount_percent?: number;
      discount_amount?: number;
      minimum_spend?: number;
    };
    rule_id: string;
    rule_name: string;
    rule_type: string;
  };
}

interface WordPressLocation {
  title: string;
  slug: string;
  locationDetails: {
    storeImage?: {
      node: {
        mediaItemUrl: string;
        altText: string;
      };
    };
    address: string;
    addressmap?: {
      latitude: number;
      longitude: number;
    };
    openingHours: string;
    phone: string;
    email: string;
    comingSoon?: string[];
    faqs?: Array<{
      question: string;
      answer: string;
    }>;
  };
}

// ⚡ PERFORMANCE: Cache FAQs per store (FAQs are unique per store, same for all products)
// TTL: 30 minutes (FAQs don't change frequently)
// Max size: 50 entries (one per store)
// Cache key: store (all products in same store share FAQs)
const faqsCache = createCache<WordPressLocation[]>(
  {
    ttl: 30 * 60 * 1000, // 30 minutes
    maxSize: 50, // One entry per store
  },
  'StoreFAQsCache',
);

// Fetch all locations from Supabase via API route
async function fetchAllLocations(): Promise<WordPressLocation[]> {
  try {
    const response = await fetch('/api/locations');
    if (!response.ok) {
      console.warn('Locations API failed');
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
}

// Function to get current store location data from all locations
function getCurrentStoreLocation(
  store: string,
  allLocations: WordPressLocation[],
): WordPressLocation | null {
  // Map store values to location slugs (same as in locations page)
  const storeToLocationSlug: Record<string, string> = {
    catalystantioch: 'antioch-dispensary',
    catalystbellflower: 'bellflower-dispensary',
    catalystbelmont: 'belmont-shore-dispensary',
    catalyst405: 'cherry-ave-long-beach-dispensary',
    catalystcostamesa: 'costa-mesa-dispensary',
    catalystdalycity: 'daly-city-dispensary',
    catalystpine: 'downtown-long-beach-dispensary',
    catalystelmonte: 'el-monte-dispensary',
    catalystfigueroa: 'figueroa-los-angeles-dispensary',
    catalystflorence: 'florence-los-angeles-dispensary',
    catalystgroverbeach: 'grover-beach-dispensary',
    catalysthawthorne: 'hawthorne-dispensary',
    catalysthemet: 'hemet-dispensary',
    catalystlancaster: 'lancaster-dispensary',
    catalystlift: 'retro-row-long-beach-dispensary',
    catalystlynwood: 'lynwood-dispensary',
    catalystmarina: 'marina-dispensary',
    catalystmidcity: 'mid-city-los-angeles-dispensary',
    catalystmorenovalley: 'moreno-valley-dispensary',
    catalystnormandie: 'normandie-los-angeles-dispensary',
    catalystoc3: 'oc3-santa-ana-dispensary',
    catalystriverside: 'riverside-dispensary',
    catalystsacramento: 'sacramento-dispensary',
    catalystsalinas: 'salinas-dispensary',
    catalystsantaana: 'santa-ana-dispensary',
    catalystsantabarbara: 'santa-barbara-dispensary',
    catalystsantacruz: 'santa-cruz-dispensary',
    catalystsantamaria: 'santa-maria-dispensary',
    catalystsimi: 'simi-valley-dispensary',
    catalystventura: 'ventura-dispensary',
    catalystwatsonville: 'watsonville-dispensary',
    catalystwesthollywood: 'west-hollywood-dispensary',
    catalystwestminster: 'westminster-dispensary',
  };

  const locationSlug = storeToLocationSlug[store];
  if (!locationSlug) {
    console.warn(`🚨 Store mapping missing for: ${store}`);
    return null;
  }

  const foundLocation = allLocations.find((location) => location.slug === locationSlug);
  if (!foundLocation) {
    console.warn(`🚨 WordPress location not found for slug: ${locationSlug} (store: ${store})`);
  }

  return foundLocation || null;
}

export default function GlobalProductModal() {
  const { isProductModalOpen, currentProductId, cachedProductData, closeProductModal } = useModal();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [allLocations, setAllLocations] = useState<WordPressLocation[]>([]);
  const [currentStoreLocation, setCurrentStoreLocation] = useState<WordPressLocation | null>(null);
  const { selectedStore } = useStore();
  const location = useLocation();
  const previousLocationRef = useRef<string>('');
  const modalWrapperRef = useRef<HTMLDivElement>(null);

  // 🔒 CHECK IF LOGIN MODAL IS OPEN: Prevent product modal from closing during login flow
  const isLoginModalOpen = () => {
    if (typeof document === 'undefined') return false;

    // Multiple detection methods for reliability:
    // 1. Check for login modal dialog elements by z-index (z-[200])
    const loginDialogs = document.querySelectorAll('[role="dialog"][class*="z-[200]"]');
    if (loginDialogs.length > 0) return true;

    // 2. Check for login dropdown by ID
    const loginDropdown = document.getElementById('login-dropdown') || document.getElementById('otp-verification');
    if (loginDropdown) return true;

    // 3. Check if login form modal is visible (has isModalOpen state)
    // This is a fallback - check for login form structure
    const loginFormModals = document.querySelectorAll(
      'form[action*="otp"], input[type="tel"][placeholder*="phone"], input[type="email"][placeholder*="email"]',
    );
    // Only consider it open if it's in a modal overlay
    for (const form of loginFormModals) {
      const parent = form.closest('[class*="fixed"][class*="inset-0"]');
      if (parent) {
        const zIndex = window.getComputedStyle(parent).zIndex;
        if (zIndex && parseInt(zIndex) >= 140) {
          return true;
        }
      }
    }

    return false;
  };

  // 🔒 SAFE CLOSE: Only close product modal if login modal is not open
  const safeCloseProductModal = () => {
    if (!isLoginModalOpen()) {
      closeProductModal();
    }
  };

  // Reset product state and announce when modal closes
  useEffect(() => {
    if (!isProductModalOpen) {
      // Announce modal closing
      setAnnouncement('Product modal closed.');
      // Small delay before clearing the product to prevent flicker if quickly reopened
      const timeout = setTimeout(() => {
        setProduct(null);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isProductModalOpen]);

  // Use focus trap hook for focus management and scroll locking
  const modalRef = useFocusTrap(isProductModalOpen);

  // Ensure modal scrolls to top when it opens and announce
  useEffect(() => {
    if (isProductModalOpen) {
      // Scroll modal wrapper to top immediately (useFocusTrap handles focus)
      if (modalWrapperRef.current) {
        modalWrapperRef.current.scrollTop = 0;
      }
      // Also ensure the modal container itself is at top
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }

      // Announce modal opening
      const announceTimer = setTimeout(() => {
        if (product) {
          setAnnouncement(`Product modal opened for ${product.name}.`);
        } else {
          setAnnouncement('Product modal opened.');
        }
      }, 200);

      return () => {
        clearTimeout(announceTimer);
      };
    }
  }, [isProductModalOpen, modalRef, product]);

  // Close on Escape while open - but NOT if login modal is open
  useEffect(() => {
    if (!isProductModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't close product modal if login modal is open (let login modal handle Escape)
        if (!isLoginModalOpen()) {
          safeCloseProductModal();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isProductModalOpen, closeProductModal]);

  // Temporarily disable mobile sidebar when product modal is open
  useEffect(() => {
    const sidebar = document.getElementById('mobile-menu-drawer');
    if (isProductModalOpen && sidebar) {
      // Only use inert - don't use aria-hidden as it conflicts with focusable elements
      sidebar.setAttribute('inert', '');
    }
    return () => {
      if (sidebar) {
        sidebar.removeAttribute('inert');
      }
    };
  }, [isProductModalOpen]);

  // Temporarily close store selector if open when product modal opens (avoid nested traps)
  useEffect(() => {
    if (isProductModalOpen) {
      const openStoreSelector = document.getElementById('store-selector-modal');
      if (openStoreSelector) {
        // Emulate outside click to close it safely
        openStoreSelector.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }
    }
  }, [isProductModalOpen]);

  // Note: we avoid using inert on background here to simplify interaction; focus trap handles containment

  // Listen for custom close event from focus trap - but NOT if login modal is open
  useEffect(() => {
    const handleClose = () => {
      // Don't close product modal if login modal is open
      if (!isLoginModalOpen()) {
        safeCloseProductModal();
      }
    };
    document.addEventListener('close-focus-trap', handleClose);
    return () => document.removeEventListener('close-focus-trap', handleClose);
  }, [closeProductModal]);

  // Listen for a custom event to close this modal - but NOT if login modal is open
  useEffect(() => {
    const handleCloseEvent = () => {
      // Don't close product modal if login modal is open
      if (!isLoginModalOpen()) {
        safeCloseProductModal();
      }
    };
    document.addEventListener('closeModals', handleCloseEvent);
    return () => {
      document.removeEventListener('closeModals', handleCloseEvent);
    };
  }, [closeProductModal]);

  // ⚡ PERFORMANCE: Fetch locations data with caching per store
  // FAQs are unique per store and same for all products, so cache once per store
  useEffect(() => {
    async function fetchLocations() {
      if (!isProductModalOpen) return;

      // ⚡ CACHE KEY: Use store (FAQs are unique per store)
      const cacheKey = createCacheKey('faqs', selectedStore);

      // Check cache first
      const cached = faqsCache.get(cacheKey);
      if (cached !== null && cached.length > 0) {
        // Cache hit - use immediately
        setAllLocations(cached);

        // Get current store location for FAQs
        const currentLocation = getCurrentStoreLocation(selectedStore, cached);
        setCurrentStoreLocation(currentLocation);
        return;
      }

      // ⚡ RACE CONDITION PREVENTION: Check if already fetching
      if (faqsCache.isFetching(cacheKey)) {
        return; // Another fetch is already in progress, skip duplicate
      }

      // Mark as fetching to prevent concurrent requests
      faqsCache.markFetching(cacheKey);

      try {
        const locations = await fetchAllLocations();

        // ⚡ CACHE THE LOCATIONS (shared across all products in same store)
        faqsCache.set(cacheKey, locations);

        setAllLocations(locations);

        // Get current store location for FAQs
        const currentLocation = getCurrentStoreLocation(selectedStore, locations);
        setCurrentStoreLocation(currentLocation);
      } catch (error) {
        console.error('Error fetching locations:', error);
        // Don't fail completely - just log and continue without location data
        setAllLocations([]);
        setCurrentStoreLocation(null);

        // Cache empty array to prevent refetch spam
        faqsCache.set(cacheKey, []);
      }
    }

    fetchLocations();
  }, [isProductModalOpen, selectedStore]);

  // Close modal on navigation
  useEffect(() => {
    if (
      isProductModalOpen &&
      previousLocationRef.current &&
      location.pathname !== previousLocationRef.current
    ) {
      // Navigation occurred - close the modal (but NOT if login modal is open)
      if (!isLoginModalOpen()) {
        safeCloseProductModal();
      }
    }
    previousLocationRef.current = location.pathname;
  }, [location.pathname, isProductModalOpen, closeProductModal]);

  // Fetch product data when currentProductId changes
  useEffect(() => {
    const controller = new AbortController();
    async function fetchProduct() {
      if (!currentProductId || !isProductModalOpen) return;

      // ⚡ INSTANT DISPLAY: If we have cached data, use it immediately
      // This allows modal to open instantly with product info already displayed
      if (cachedProductData && cachedProductData.id === currentProductId) {
        // Convert cached data to Product format for instant display
        const cachedProduct = {
          id: cachedProductData.id,
          name: cachedProductData.name,
          strain: cachedProductData.strain,
          image: cachedProductData.image || '',
          primary_image: cachedProductData.primary_image,
          brand: cachedProductData.brand || '',
          category: cachedProductData.category || '',
          price: cachedProductData.price,
          salePrice: cachedProductData.salePrice,
          loyaltyTierLabel: cachedProductData.loyaltyTierLabel,
          discount: cachedProductData.discount,
          product_description: cachedProductData.product_description || '',
          thc: cachedProductData.thc || '',
          size: cachedProductData.size || '',
          classification: cachedProductData.classification || '',
          subtype: '',
          lab_results: '',
          slug: cachedProductData.slug,
          budflow_mup: cachedProductData.budflow_mup,
        };
        setProduct(cachedProduct as Product);
        // Don't set loading to true - we already have something to show
      } else {
        // Clear previous product and show loading state
        setIsLoading(true);
        setProduct(null);
      }

      try {
        const response = await fetch(
          `/api/product?productId=${currentProductId}&store=${selectedStore}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          // If product doesn't exist in this store, close modal (but NOT if login modal is open)
          if (response.status === 404) {
            console.warn(`Product ${currentProductId} not found in store ${selectedStore}`);
            if (!isLoginModalOpen()) {
              safeCloseProductModal();
            }
            return;
          }
          throw new Error('Failed to fetch product details');
        }
        const data = await response.json();
        if (controller.signal.aborted) return;

        // Check if response contains an error
        if (data.error) {
          // If product doesn't exist in this store, close modal (but NOT if login modal is open)
          if (data.error.includes('not found') || data.error.includes('Not found')) {
            console.warn(`Product ${currentProductId} not found in store ${selectedStore}`);
            if (!isLoginModalOpen()) {
              safeCloseProductModal();
            }
            return;
          }
          throw new Error(data.error);
        }

        setProduct(data);

        // Product loaded, clear any previous announcements
        setAnnouncement('');

        // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
        if (data) {
          analytics.trackViewItemComplete({
            id: data.id.toString(),
            name: data.name,
            category: data.category,
            brand: data.brand,
            price: data.salePrice || data.price,
          });

          // Virtual page view for GTM (NPDigital G02)
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'virtual_page_view',
            page_path: `/product/${data.slug}`,
            page_title: data.name,
          });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error fetching product details:', error);
        // If error suggests product doesn't exist, close modal (but NOT if login modal is open)
        if (
          error instanceof Error &&
          (error.message.includes('not found') || error.message.includes('Not found'))
        ) {
          if (!isLoginModalOpen()) {
            safeCloseProductModal();
          }
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    fetchProduct();
    return () => controller.abort();
  }, [currentProductId, selectedStore, isProductModalOpen, closeProductModal, cachedProductData]);

  // Handle sharing
  const handleShare = async () => {
    if (!product) return;

    const shareUrl = `${window.location.origin}/product/${product.slug}`;
    const shareText = `Check out this ${product.name} by ${product.brand}`;

    // Check if Web Share API is supported and can share URLs
    if (navigator.share && navigator.canShare && navigator.canShare({ url: shareUrl })) {
      try {
        await navigator.share({
          title: product.name,
          text: shareText,
          url: shareUrl,
        });
        return; // Success, exit early
      } catch (error) {
        // User cancelled or share failed, fall back to clipboard
        console.error('Native share failed:', error);
      }
    }

    // Fallback: Copy to clipboard with better error handling
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        // Fallback for older browsers - create temporary input
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const success = document.execCommand('copy');
          if (success) {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
  };

  // Don't render anything if the modal is not open
  if (!isProductModalOpen) return null;

  // Show loading state while fetching product
  if (isLoading || !product) {
    return createPortal(
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-900/70">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={modalWrapperRef}
      id="modal-wrapper"
      className="fixed inset-0 z-[110] overflow-auto"
      data-product-id={currentProductId || ''}
    >
      {/* ARIA Live Region for Modal Announcements */}
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[110] bg-gray-900/70" onClick={safeCloseProductModal} />
      {/* Modal Wrapper - Match Embarc's width */}
      <div
        ref={modalRef}
        id="product-modal"
        tabIndex={-1}
        className="relative z-[120] mx-auto mt-6 flex w-full max-w-[76.4rem] flex-col overflow-x-hidden overflow-y-auto px-2 pb-20 lg:px-4"
        role="dialog"
        aria-modal="true"
        aria-label={product ? `Product modal for ${product.name}` : 'Product modal'}
      >
        <div className="relative z-70">
          <nav className="flex justify-between bg-white p-5" aria-label="Product modal navigation">
            <div className="flex items-center gap-3">
              <a
                href={`/product/${product.slug}`}
                rel="noopener noreferrer"
                aria-label={`View ${product.name} full product page`}
                className="relative inline-flex items-center justify-center rounded-full p-1 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
              >
                <svg
                  width="30"
                  height="20"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 512"
                >
                  <path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z" />
                </svg>
              </a>
              <button
                onClick={handleShare}
                className="flex size-10 items-center justify-center transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:outline-none"
                aria-label={linkCopied ? 'Link copied!' : 'Share this product'}
                title={linkCopied ? 'Link copied!' : 'Share this product'}
              >
                {linkCopied ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="#22c55e"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="16,6 12,2 8,6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line
                      x1="12"
                      y1="2"
                      x2="12"
                      y2="15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            <button
              className="close-modal relative inline-flex items-center justify-center rounded-full p-1 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
              onClick={safeCloseProductModal}
              aria-label="Close product modal and return to previous page"
            >
              <img src="/icons/X-Icon-white.svg" alt="Close icon" aria-hidden="true" decoding="async" />
            </button>
          </nav>
        </div>
        <div className="relative mt-0 w-full bg-white">
          {/* JSON-LD structured data */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: product.name || 'Unknown Product',
                image: product.image || '/images/placeholder-image.webp',
                brand: {
                  '@type': 'Brand',
                  name: product.brand || 'Unknown Brand',
                },
                sku: product.id,
                category: product.category,
                description:
                  product.product_description ||
                  `${product.brand || 'Premium'} ${product.name || 'cannabis product'}`,
                offers: {
                  '@type': 'Offer',
                  price: product.salePrice || product.price,
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                  url: `/product/${product.slug}`,
                  seller: {
                    '@type': 'Organization',
                    name: 'Catalyst Cannabis',
                  },
                },
              }),
            }}
          />
          <ModalProductCard
            onClose={closeProductModal}
            hideNav={true}
            product={product}
            currentStoreLocation={currentStoreLocation}
          />
          <div className="w-full">
            <ShopNearbyDispensaries
              currentStore={selectedStore}
              allLocations={allLocations}
              onNavigate={closeProductModal}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
